import type { ReactNode } from "react"
import { Card } from "@alascorporation/ui"
import {
  COUNTRIES,
  GENDER_OPTIONS,
  BLOOD_TYPES,
  ACADEMIC_LEVEL_OPTIONS,
  getStatesForCountry,
  getCitiesForState,
} from "@alascorporation/utils"
import { inputCls, labelCls, StyledSelect, Toggle } from "./FormComponents"

export type StudentProfileFields = {
  firstName?: string | null
  lastName?: string | null
  documentType?: string | null
  documentNumber?: string | null
  birthDate?: string | null
  gender?: "M" | "F" | "NB" | "ND" | null
  countryCode?: string | null
  residenceCountryCode?: string | null
  stateProvince?: string | null
  city?: string | null
  address?: string | null
  guardianName?: string | null
  guardianRelationship?: string | null
  guardianDocumentType?: string | null
  guardianDocumentNumber?: string | null
  guardianPhone?: string | null
  guardianEmail?: string | null
  bloodType?: string | null
  hasDisability?: boolean | null
  disabilityDetail?: string | null
  medications?: string | null
  institution?: string | null
  selfDeclaredRole?: string | null
  academicLevel?: string | null
}

export type SectionId = "personal" | "document" | "location" | "health" | "guardian" | "institution"
export type SectionStatus = "complete" | "partial" | "empty"

export const SECTION_FIELD_KEYS: Record<SectionId, (keyof StudentProfileFields)[]> = {
  personal: ["firstName", "lastName", "birthDate", "gender"],
  document: ["countryCode", "documentType", "documentNumber"],
  location: ["residenceCountryCode", "address", "stateProvince", "city"],
  // Only bloodType is required for this card to read as "complete" — disability info and
  // medications are optional fields for students who need to report them, not a universal requirement.
  health: ["bloodType"],
  guardian: [
    "guardianName",
    "guardianRelationship",
    "guardianDocumentType",
    "guardianDocumentNumber",
    "guardianPhone",
    "guardianEmail",
  ],
  institution: ["institution", "selfDeclaredRole", "academicLevel"],
}

const ALL_FIELD_KEYS = Object.values(SECTION_FIELD_KEYS).flat()

function isFilled(value: unknown): boolean {
  return value !== null && value !== undefined && value !== ""
}

export function sectionStatus(
  profile: StudentProfileFields,
  id: SectionId,
): { status: SectionStatus; filled: number; total: number } {
  const keys = SECTION_FIELD_KEYS[id]
  const filled = keys.filter((key) => isFilled(profile[key])).length
  if (filled === 0) return { status: "empty", filled, total: keys.length }
  if (filled === keys.length) return { status: "complete", filled, total: keys.length }
  return { status: "partial", filled, total: keys.length }
}

export function completionPercent(profile: StudentProfileFields): number {
  const filled = ALL_FIELD_KEYS.filter((key) => isFilled(profile[key])).length
  return Math.round((filled / ALL_FIELD_KEYS.length) * 100)
}

export type CompletionTone = "good" | "warning" | "danger"

export function completionTone(percent: number): CompletionTone {
  if (percent >= 80) return "good"
  if (percent >= 40) return "warning"
  return "danger"
}

export const COMPLETION_TONE_CLASS: Record<CompletionTone, string> = {
  good: "text-success-600 dark:text-success-500",
  warning: "text-warning-600 dark:text-orange-400",
  danger: "text-error-600 dark:text-error-500",
}

// ── Icons (20px grid, stroke, currentColor) ─────────────────────────────────

export const SECTION_ICONS: Record<SectionId, ReactNode> = {
  personal: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  document: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="8.5" cy="12" r="1.8" stroke="currentColor" strokeWidth="1.5" />
      <path d="M13 10h5M13 14h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  location: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 21s-7-6.2-7-11a7 7 0 1114 0c0 4.8-7 11-7 11z" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  health: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 20s-7.5-4.6-7.5-10A4.5 4.5 0 0112 6.5 4.5 4.5 0 0119.5 10c0 5.4-7.5 10-7.5 10z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  ),
  guardian: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <circle cx="8.5" cy="9" r="2.6" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="16" cy="10" r="2.1" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3.5 19c.4-3 2.5-4.8 5-4.8s4.6 1.8 5 4.8M14.3 14.6c2 .2 3.5 1.8 3.8 4.4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  institution: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 4l9 4.5-9 4.5-9-4.5L12 4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M6 10.5V16c0 1 2.5 2.5 6 2.5s6-1.5 6-2.5v-5.5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
}

export function StatusBadge({ status, filled, total }: { status: SectionStatus; filled: number; total: number }) {
  if (status === "complete") {
    return (
      <span className="rounded-full bg-success-50 px-2.5 py-0.5 text-[11px] font-semibold text-success-700 dark:bg-success-500/15 dark:text-success-300 whitespace-nowrap">
        Completo
      </span>
    )
  }
  if (status === "partial") {
    return (
      <span className="rounded-full bg-warning-50 px-2.5 py-0.5 text-[11px] font-semibold text-warning-700 dark:bg-warning-500/15 dark:text-warning-300 whitespace-nowrap">
        {filled} de {total}
      </span>
    )
  }
  return (
    <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-white/[0.06] dark:text-gray-400 whitespace-nowrap">
      Sin iniciar
    </span>
  )
}

export const SECTION_TITLES: Record<SectionId, string> = {
  personal: "Datos personales",
  document: "Documento de identidad",
  location: "Ubicación",
  health: "Salud",
  guardian: "Acudiente",
  institution: "Institución y formación",
}

export const SECTION_ORDER: SectionId[] = ["personal", "document", "location", "health", "guardian", "institution"]

export function SectionCard({
  id: sectionId,
  profile,
  children,
}: {
  id: SectionId
  profile: StudentProfileFields
  children: ReactNode
}) {
  const { status, filled, total } = sectionStatus(profile, sectionId)
  return (
    <Card className="overflow-hidden rounded-[22px]">
      <div className="w-full flex items-center gap-3 border-b border-border px-5 py-4 text-left">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-muted text-primary">
          {SECTION_ICONS[sectionId]}
        </span>
        <span className="flex-1 text-base font-semibold text-foreground">
          {SECTION_TITLES[sectionId]}
        </span>
        <StatusBadge status={status} filled={filled} total={total} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-5 pb-5 pt-1">{children}</div>
    </Card>
  )
}

export function StudentProfileSections({
  profile,
  set,
  documentTypes,
  activeSection,
  personalExtra,
}: {
  profile: StudentProfileFields
  set: <K extends keyof StudentProfileFields>(key: K, value: StudentProfileFields[K]) => void
  documentTypes: { value: string; label: string }[]
  activeSection: SectionId
  /** Extra fields rendered at the top of the "Datos personales" section (e.g. email, only needed while creating). */
  personalExtra?: ReactNode
}) {
  const cardProps = (id: SectionId) => ({ id, profile })

  return (
    <>
      {activeSection === "personal" && (
      <SectionCard {...cardProps("personal")}>
        {personalExtra}
        <div>
          <label className={labelCls}>Nombres</label>
          <input className={inputCls} value={profile.firstName ?? ""} onChange={(e) => set("firstName", e.target.value || null)} />
        </div>
        <div>
          <label className={labelCls}>Apellidos</label>
          <input className={inputCls} value={profile.lastName ?? ""} onChange={(e) => set("lastName", e.target.value || null)} />
        </div>
        <div>
          <label className={labelCls}>Fecha de nacimiento</label>
          <input
            type="date"
            className={inputCls}
            value={profile.birthDate ?? ""}
            onChange={(e) => set("birthDate", e.target.value || null)}
          />
        </div>
        <div>
          <label className={labelCls}>Género</label>
          <StyledSelect
            value={profile.gender ?? ""}
            onChange={(e) => set("gender", (e.target.value || null) as StudentProfileFields["gender"])}
          >
            <option value="">Selecciona...</option>
            {GENDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </StyledSelect>
        </div>
      </SectionCard>
      )}

      {activeSection === "document" && (
      <SectionCard {...cardProps("document")}>
        <div>
          <label className={labelCls}>País (documento)</label>
          <StyledSelect value={profile.countryCode ?? ""} onChange={(e) => set("countryCode", e.target.value || null)}>
            <option value="">Selecciona...</option>
            {COUNTRIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </StyledSelect>
        </div>
        <div>
          <label className={labelCls}>Tipo de documento</label>
          <StyledSelect value={profile.documentType ?? ""} onChange={(e) => set("documentType", e.target.value || null)}>
            <option value="">Selecciona...</option>
            {documentTypes.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </StyledSelect>
        </div>
        <div>
          <label className={labelCls}>Número de documento</label>
          <input
            className={inputCls}
            value={profile.documentNumber ?? ""}
            onChange={(e) => set("documentNumber", e.target.value || null)}
          />
        </div>
      </SectionCard>
      )}

      {activeSection === "location" && (
      <SectionCard {...cardProps("location")}>
        {(() => {
          const states = getStatesForCountry(profile.residenceCountryCode)
          const selectedState = states.find((s) => s.value === profile.stateProvince)
          const cities = getCitiesForState(profile.residenceCountryCode, selectedState?.isoCode)
          return (
            <>
              <div className="md:col-span-2">
                <label className={labelCls}>País de residencia</label>
                <StyledSelect
                  value={profile.residenceCountryCode ?? ""}
                  onChange={(e) => {
                    set("residenceCountryCode", e.target.value || null)
                    set("stateProvince", null)
                    set("city", null)
                  }}
                >
                  <option value="">Selecciona...</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </StyledSelect>
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Dirección de residencia</label>
                <input className={inputCls} value={profile.address ?? ""} onChange={(e) => set("address", e.target.value || null)} />
              </div>
              <div>
                <label className={labelCls}>Departamento / Estado / Provincia</label>
                {states.length > 0 ? (
                  <StyledSelect
                    value={profile.stateProvince ?? ""}
                    onChange={(e) => {
                      set("stateProvince", e.target.value || null)
                      set("city", null)
                    }}
                  >
                    <option value="">Selecciona...</option>
                    {states.map((s) => (
                      <option key={s.isoCode} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </StyledSelect>
                ) : (
                  <input
                    className={inputCls}
                    value={profile.stateProvince ?? ""}
                    onChange={(e) => set("stateProvince", e.target.value || null)}
                    disabled={!profile.residenceCountryCode}
                    placeholder={profile.residenceCountryCode ? "" : "Selecciona primero el país"}
                  />
                )}
              </div>
              <div>
                <label className={labelCls}>Municipio / Ciudad</label>
                {cities.length > 0 ? (
                  <StyledSelect value={profile.city ?? ""} onChange={(e) => set("city", e.target.value || null)}>
                    <option value="">Selecciona...</option>
                    {cities.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </StyledSelect>
                ) : (
                  <input
                    className={inputCls}
                    value={profile.city ?? ""}
                    onChange={(e) => set("city", e.target.value || null)}
                    disabled={states.length > 0 && !selectedState}
                    placeholder={states.length > 0 && !selectedState ? "Selecciona primero el departamento" : ""}
                  />
                )}
              </div>
            </>
          )
        })()}
      </SectionCard>
      )}

      {activeSection === "health" && (
      <SectionCard {...cardProps("health")}>
        <div>
          <label className={labelCls}>Grupo sanguíneo</label>
          <StyledSelect value={profile.bloodType ?? ""} onChange={(e) => set("bloodType", e.target.value || null)}>
            <option value="">Selecciona...</option>
            {BLOOD_TYPES.map((bt) => (
              <option key={bt} value={bt}>
                {bt}
              </option>
            ))}
          </StyledSelect>
        </div>
        <div className="flex items-center md:pt-6">
          <Toggle
            checked={profile.hasDisability ?? false}
            onChange={(checked) => {
              set("hasDisability", checked)
              if (!checked) set("disabilityDetail", null)
            }}
            label="Tiene alguna discapacidad"
          />
        </div>
        {profile.hasDisability && (
          <div className="md:col-span-2">
            <label className={labelCls}>¿Cuál es su discapacidad?</label>
            <input
              className={inputCls}
              value={profile.disabilityDetail ?? ""}
              onChange={(e) => set("disabilityDetail", e.target.value || null)}
            />
          </div>
        )}
        <div className="md:col-span-2">
          <label className={labelCls}>Si toma algún medicamento, escríbalo aquí</label>
          <input className={inputCls} value={profile.medications ?? ""} onChange={(e) => set("medications", e.target.value || null)} />
        </div>
      </SectionCard>
      )}

      {activeSection === "guardian" && (
      <SectionCard {...cardProps("guardian")}>
        <div>
          <label className={labelCls}>Nombre completo</label>
          <input
            className={inputCls}
            value={profile.guardianName ?? ""}
            onChange={(e) => set("guardianName", e.target.value || null)}
          />
        </div>
        <div>
          <label className={labelCls}>Parentesco</label>
          <input
            className={inputCls}
            value={profile.guardianRelationship ?? ""}
            onChange={(e) => set("guardianRelationship", e.target.value || null)}
          />
        </div>
        <div>
          <label className={labelCls}>Tipo de documento</label>
          <StyledSelect
            value={profile.guardianDocumentType ?? ""}
            onChange={(e) => set("guardianDocumentType", e.target.value || null)}
          >
            <option value="">Selecciona...</option>
            {documentTypes.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </StyledSelect>
        </div>
        <div>
          <label className={labelCls}>Número de documento</label>
          <input
            className={inputCls}
            value={profile.guardianDocumentNumber ?? ""}
            onChange={(e) => set("guardianDocumentNumber", e.target.value || null)}
          />
        </div>
        <div>
          <label className={labelCls}>Número de celular</label>
          <input
            className={inputCls}
            value={profile.guardianPhone ?? ""}
            onChange={(e) => set("guardianPhone", e.target.value || null)}
          />
        </div>
        <div>
          <label className={labelCls}>Correo electrónico</label>
          <input
            type="email"
            className={inputCls}
            value={profile.guardianEmail ?? ""}
            onChange={(e) => set("guardianEmail", e.target.value || null)}
          />
        </div>
      </SectionCard>
      )}

      {activeSection === "institution" && (
      <SectionCard {...cardProps("institution")}>
        <div>
          <label className={labelCls}>Institución educativa</label>
          <input
            className={inputCls}
            value={profile.institution ?? ""}
            onChange={(e) => set("institution", e.target.value || null)}
          />
        </div>
        <div>
          <label className={labelCls}>Rol en el equipo</label>
          <input
            className={inputCls}
            value={profile.selfDeclaredRole ?? ""}
            onChange={(e) => set("selfDeclaredRole", e.target.value || null)}
          />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Nivel de formación académica</label>
          <StyledSelect value={profile.academicLevel ?? ""} onChange={(e) => set("academicLevel", e.target.value || null)}>
            <option value="">Selecciona...</option>
            {ACADEMIC_LEVEL_OPTIONS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </StyledSelect>
        </div>
      </SectionCard>
      )}
    </>
  )
}
